import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { AffiliateApplicationFormConfig, PutFormConfigDTO } from '../domain/affiliate-application-types';
import { affiliateApplicationService } from '../infra/affiliateApplicationService';
import { transformAffiliateApplicationFormConfig } from '../infra/mappers';

interface SaveFormConfigParams {
  projectId: string;
  dto: PutFormConfigDTO;
}

interface UseSaveFormConfigResult {
  saveFormConfig: (params: SaveFormConfigParams) => void;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  error: Error | null;
}

export const useSaveFormConfig = (): UseSaveFormConfigResult => {
  const queryClient = useQueryClient();

  const {
    mutate,
    isPending,
    isError,
    isSuccess,
    error,
  }: UseMutationResult<AffiliateApplicationFormConfig, Error, SaveFormConfigParams> = useMutation({
    mutationFn: async ({ projectId, dto }: SaveFormConfigParams) => {
      const response = await affiliateApplicationService.putFormConfig(projectId, dto);
      return transformAffiliateApplicationFormConfig(response);
    },
    onSuccess: (data, variables) => {
      toast.success('Form configuration saved');
      queryClient.invalidateQueries({
        queryKey: ['affiliate-application-form-config', variables.projectId],
      });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to save form configuration');
    },
  });

  return {
    saveFormConfig: mutate,
    isLoading: isPending,
    isError,
    isSuccess,
    error,
  };
};
